import { Button, IconBulletPoint, P, Skeleton } from "@template/components";
import * as S from "./styles.ts";
import { ReactElement, useEffect, useState } from "react";
import { useTypedSelector } from "@/redux/helpers/hooks.ts";
import { useAddPlan, useUpgradePlan } from "@/lib/api/plan";
import useCurrency from "@/lib/helpers/hooks/useCurrency.ts";
import { useRouter } from "@/lib/helpers/hooks/useRouter.ts";
import { IPillProps } from "@template/components/Pill";
import { IButtonProps } from "@template/components/Button";
import ContactModal from "../ContactModal/index.tsx";
import { TBillingInterval } from "@/redux/slices/user/types.ts";

export type TPricingCard = {
  type: number;
  features?: string[];
  pills?: IPillProps[];
  copy?: string;
  list?: TPricingCardListItem[];
  footer?: string | ReactElement;
  button?: IButtonProps;
};

type TPricingCardListItem = {
  label: string;
  iconName?: string;
  badge?: IPillProps;
  tooltip?: string;
};

export interface IPricingCardProps extends TPricingCard {
  userCount: number;
  loading?: boolean;
  renewalPeriod?: TBillingInterval;
}

const PricingCardHeader = ({
  type,
  userCount,
  renewalPeriod,
}: {
  type: IPricingCardProps["type"];
  userCount: number;
  renewalPeriod: TBillingInterval;
}) => {
  const { plans, loaded } = useTypedSelector(
    (state) => state.store.plan_prices
  );

  const currency = useCurrency();

  const plan = plans.find((p) => p.id === type + 1);
  const planPrices = plan?.costs;

  const planPrice = loaded
    ? renewalPeriod === "monthly"
      ? ((planPrices?.monthly || 0) / 100)?.toFixed(2)
      : ((planPrices?.yearly || 0) / (12 * 100))?.toFixed(2)
    : null;

  const pillValue = (plan) => {
    if (currency === "$") return "Save 20%";
    if (plan?.name === "Standard") return "Save 20%";
    if (plan?.name === "Premium") return "Save 16%";
    return "Save 25%";
  };

  return (
    <>
      <S.PricingCardPlanText>{plan?.name}</S.PricingCardPlanText>
      <S.PricingTextContainer>
        <Skeleton hideSkeleton={loaded}>
          <S.PricingTextWrapper>
            <p>{currency}</p>
            {loaded ? planPrice : "0.00"}
          </S.PricingTextWrapper>
        </Skeleton>
        <S.CurrencyContainer>
          <div>Per User / mth</div>
          <div>
            {renewalPeriod === "yearly" ? (
              <>paid yearly</>
            ) : (
              <>
                <S.PillSpan>{pillValue(plan)}</S.PillSpan>
                &nbsp;when billed yearly
              </>
            )}
          </div>
        </S.CurrencyContainer>
      </S.PricingTextContainer>
    </>
  );
};

const PricingCard = ({
  type,
  features,
  pills,
  list,
  footer,
  userCount,
  button,
  loading: planLoading,
  copy,
  renewalPeriod,
}: IPricingCardProps) => {
  const router = useRouter();
  const userState = useTypedSelector((state) => state.user);
  const [showModal, setShowModal] = useState(false);
  const cart = userState.cart;
  const user = userState.user;
  const cartItems = cart.items;

  const userPlan = user.voip_plan;
  const isExistingPlan = userPlan === type + 1;
  const isDowngrade = userPlan && userPlan > type + 1;
  const isUpgrade = userPlan && userPlan < type + 1;

  const addToCartBtnText = userPlan
    ? isUpgrade
      ? "Upgrade Plan"
      : isDowngrade
        ? "Downgrade Plan"
        : "Buy Now"
    : "Buy Now";

  const plansInCart = cartItems.filter((item) => item.type.id === 1);

  const planInCart = plansInCart.some(
    (p) => p.type.sub_type_id === type && p.period === userCount
  );

  const addPlan = useAddPlan();
  const upgradePlan = useUpgradePlan();

  const [loading, setLoading] = useState(false);

  const onAdd = async () => {
    setLoading(true);
    if (user?.has_voip) {
      router.push("/account/member/plans");
      //  await upgradePlan({
      //   planId: type + 1,
      //   quantity: userCount,
      //   billing_interval: renewalPeriod,
      // }).then(() => {
      //   if (cart.items.some((x) => x.type.name === "Number") || user.has_voip) {
      //     router.push("/shopping-cart");
      //   } else {
      //     router.push("/phone-number/all-numbers");
      //   }
      // });
    } else {
      await addPlan({
        planId: type + 1,
        quantity: userCount,
        billing_interval: renewalPeriod,
      }).then(() => {
        if (cart.items.some((x) => x.type.name === "Number") || user.has_voip) {
          router.push("/shopping-cart");
        } else {
          router.push("/phone-number/all-numbers");
        }
      });
    }
    setLoading(false);
  };
  const [freePhoneNumbers, setFreePhoneNumbers] = useState(1);

  const getFreePhoneNumbersAmount = (
    userCount: number,
    type: number
  ): number => {
    if (type < 8 || type > 9) {
      throw new Error("Invalid type provided. Type must be 8 or 9.");
    }

    // Define the thresholds and corresponding values for each type
    const thresholds = [
      { limit: 2, values: { 8: 1, 9: 1 } },
      { limit: 20, values: { 8: 1, 9: 2 } },
      { limit: 100, values: { 8: 5, 9: 10 } },
      { limit: Infinity, values: { 8: 10, 9: 25 } },
    ];

    // Determine the correct set of values based on userCount
    const { values } =
      thresholds.find(({ limit }) => userCount < limit) ||
      thresholds[thresholds.length - 1];

    // Return the value corresponding to the type
    return values[type];
  };

  useEffect(() => {
    if (planLoading) return;

    switch (type) {
      case 8:
      case 9:
        setFreePhoneNumbers(getFreePhoneNumbersAmount(userCount, type));
        break;
      case 10:
        setFreePhoneNumbers(userCount);
    }
  }, [userCount]);

  return (
    <>
      {showModal && <ContactModal onClose={() => setShowModal(false)} />}
      <S.PricingCard>
        <Skeleton hideSkeleton={!planLoading}>
          <PricingCardHeader
            type={type}
            userCount={userCount}
            renewalPeriod={renewalPeriod}
          />
        </Skeleton>

        <Skeleton hideSkeleton={!planLoading}>
          <S.PricingCardCopyText>
            <P $small={true}>{copy}</P>
          </S.PricingCardCopyText>
        </Skeleton>

        <Skeleton hideSkeleton={!planLoading}>
          {isExistingPlan ? (
            <Button
              fullWidth
              size="lg"
              {...button}
              loading={loading}
              disabled
            >
              Your Plan
            </Button>
          ) : planInCart ? (
            <Button
              fullWidth
              size="lg"
              {...button}
              onClick={onAdd}
              loading={loading}
              disabled
            >
              In Cart
            </Button>
          ) : userCount > 49 ? (
            <Button
              fullWidth
              size="lg"
              {...button}
              onClick={() => setShowModal(true)}
              loading={loading}
            >
              Talk to Sales
            </Button>
          ) : (
            <Button
              fullWidth
              size="lg"
              {...button}
              onClick={onAdd}
              loading={loading}
            >
              {addToCartBtnText}
            </Button>
          )}
        </Skeleton>
        <Skeleton hideSkeleton={!planLoading}>
          {features?.length && (
            <S.SplitCard>
              {features.map((feature, index) => (
                <S.SplitCardItem key={index}>{feature}</S.SplitCardItem>
              ))}
              <S.SplitCardItem>
                {freePhoneNumbers} Phone{" "}
                {freePhoneNumbers === 1 ? "Number" : "Numbers"} Included
              </S.SplitCardItem>
            </S.SplitCard>
          )}
        </Skeleton>
        <Skeleton hideSkeleton={!planLoading}>
          {list?.map((feature, index) => (
            <IconBulletPoint
              key={index}
              item={feature}
              disable={feature?.tooltip && true}
            />
          ))}
        </Skeleton>
        <Skeleton hideSkeleton={!planLoading}>
          {footer && <S.FooterWrapper>{footer}</S.FooterWrapper>}
        </Skeleton>
      </S.PricingCard>
    </>
  );
};

export default PricingCard;
